/**
 * Parses a CSV of users for the "Specific User(s)" notification target.
 *
 * Expected columns (header row, case-insensitive, any order):
 *   - name   : display name of the user (optional value per row)
 *   - number : phone number / username sent in the payload (required)
 *
 * `number` also matches headers like "mobile", "phone" or "phone_number".
 * If no recognizable header is found, the file is treated as headerless and
 * the first column is taken as `name`, the second as `number`.
 *
 * A valid number is exactly 10 digits (after stripping spaces, dashes and
 * parentheses), matching the manual search input.
 *
 * A file may contain at most MAX_CSV_ROWS non-empty rows, including the
 * column-name row.
 *
 * Returns { rows, errors, limitExceeded? } where:
 *   - rows         : [{ name, number }] for every row with a valid 10-digit number
 *   - errors       : [{ line, value, reason }] for rows skipped because the
 *                    number is missing or not a valid 10-digit number (line is
 *                    1-based and refers to the original file, including
 *                    blank/header lines)
 *   - limitExceeded: { max, count } when the row limit is exceeded; in that
 *                    case rows and errors are empty and nothing is parsed
 */
const NAME_HEADERS = ['name', 'full_name', 'fullname']
const NUMBER_HEADERS = ['number', 'mobile', 'phone', 'phone_number', 'mobile_number']

// Splits a single CSV line, honouring double-quoted fields (which may contain commas).
const splitCsvLine = (line) => {
  const fields = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += char
      }
    } else if (char === '"') {
      inQuotes = true
    } else if (char === ',') {
      fields.push(current)
      current = ''
    } else {
      current += char
    }
  }
  fields.push(current)
  return fields.map((f) => f.trim())
}

const normalizeNumber = (value) => (value ? value.replace(/[\s\-()]/g, '') : '')

const isValidNumber = (number) => /^\d{10}$/.test(number)

// Maximum non-empty rows allowed, including the column-name (header) row.
export const MAX_CSV_ROWS = 1001

export const parseUserCsv = (text) => {
  // Keep original line numbers (1-based) so errors can point at the source file.
  const rawLines = (text || '').split(/\r\n|\r|\n/)
  const dataLines = rawLines
    .map((line, index) => ({ line: index + 1, text: line.trim() }))
    .filter((l) => l.text.length > 0)

  if (!dataLines.length) {
    return { rows: [], errors: [] }
  }

  if (dataLines.length > MAX_CSV_ROWS) {
    return {
      rows: [],
      errors: [],
      limitExceeded: { max: MAX_CSV_ROWS, count: dataLines.length },
    }
  }

  const firstCells = splitCsvLine(dataLines[0].text).map((c) => c.toLowerCase())
  const hasHeader =
    firstCells.some((c) => NAME_HEADERS.includes(c)) ||
    firstCells.some((c) => NUMBER_HEADERS.includes(c))

  let nameIdx = 0
  let numberIdx = 1
  let startIdx = 0

  if (hasHeader) {
    startIdx = 1
    const foundName = firstCells.findIndex((c) => NAME_HEADERS.includes(c))
    const foundNumber = firstCells.findIndex((c) => NUMBER_HEADERS.includes(c))
    nameIdx = foundName === -1 ? 0 : foundName
    numberIdx = foundNumber === -1 ? 1 : foundNumber
  }

  const rows = []
  const errors = []

  for (let i = startIdx; i < dataLines.length; i++) {
    const { line, text: lineText } = dataLines[i]
    const cells = splitCsvLine(lineText)
    const number = normalizeNumber(cells[numberIdx])
    const name = (cells[nameIdx] || '').trim()

    if (!number) {
      errors.push({ line, value: '', reason: 'missing number' })
      continue
    }
    if (!isValidNumber(number)) {
      errors.push({
        line,
        value: number,
        reason: 'number must be exactly 10 digits',
      })
      continue
    }
    rows.push({ name, number })
  }

  return { rows, errors }
}
